import { isDefined } from "@clipboard-health/util-ts";
import { addAppUrlOpenListener, type AppUrlOpenEvent } from "@src/appV2/NativeAdapters/app.adapter";

import { Platform } from "./platform";

type DeepLinkHandler = (data: { url: string }) => void;

const appUrlOpenQueue: string[] = [];
let urlOpenHandler: DeepLinkHandler | undefined;

/**
 * localStorage key for time-bounded deep link deduplication.
 *
 * Capacitor's native-to-web bridge re-delivers the last appUrlOpen event
 * whenever the bridge reconnects — which happens on every
 * window.location.reload(). Without deduplication, a page reload (e.g.
 * from account switching) replays the original magic-link URL, navigating
 * to AuthRedirect with an expired token.
 *
 * Stores `{ url, ts }` JSON so that identical URLs are only suppressed
 * within a short time window. After the window expires, the same URL is
 * treated as a new deep link (handles edge cases like re-sent magic links).
 *
 * Uses localStorage because Capacitor's WKWebView does not reliably
 * persist sessionStorage across reloads.
 */
const LAST_HANDLED_DEEP_LINK_KEY = "deepLinkLastHandledUrl";

/** Suppress duplicate deep links within this window (ms). */
const DEDUPE_WINDOW_MS = 30_000;

interface DeepLinkEntry {
  url: string;
  ts: number;
}

export function isDuplicate(rawUrl: string): boolean {
  try {
    const stored = localStorage.getItem(LAST_HANDLED_DEEP_LINK_KEY);
    if (!stored) {
      return false;
    }
    const entry: DeepLinkEntry = JSON.parse(stored);
    return entry.url === rawUrl && Date.now() - entry.ts <= DEDUPE_WINDOW_MS;
  } catch {
    return false;
  }
}

export function markHandled(rawUrl: string): void {
  const entry: DeepLinkEntry = { url: rawUrl, ts: Date.now() };
  localStorage.setItem(LAST_HANDLED_DEEP_LINK_KEY, JSON.stringify(entry));
}

/**
 * Module-scoped listener for deep link URL open events.
 *
 * Registered at import time (before React mounts) to capture cold-start
 * deep links that fire before the handler hook is ready.
 * Events are deduplicated and queued until `setDeepLinkHandler` is called.
 */
if (Platform.isMobile) {
  void addAppUrlOpenListener((event: AppUrlOpenEvent) => {
    if (!event.url || isDuplicate(event.url)) {
      return;
    }
    markHandled(event.url);

    if (isDefined(urlOpenHandler)) {
      urlOpenHandler({ url: event.url });
    } else {
      appUrlOpenQueue.push(event.url);
    }
  });
}

/**
 * Set the handler for deep link URL open events.
 * Drains any queued URLs from before the handler was ready,
 * then routes future events directly to the handler.
 */
export function setDeepLinkHandler(handler: DeepLinkHandler): void {
  for (const url of appUrlOpenQueue) {
    try {
      handler({ url });
    } catch {
      // Swallow errors from individual queued events so the remaining
      // events are still drained and the handler is assigned.
    }
  }
  appUrlOpenQueue.length = 0;
  urlOpenHandler = handler;
}

/**
 * Remove the current deep link handler.
 * Future events will be queued again until a new handler is set.
 */
export function clearDeepLinkHandler(): void {
  urlOpenHandler = undefined;
}

/**
 * Reset queue state. Only for use in tests.
 */
export function resetDeepLinkQueue(): void {
  appUrlOpenQueue.length = 0;
  urlOpenHandler = undefined;
}
